// Hours-based math for the Capacity Planner tab. Utilization owns the percentages; this one speaks
// only in hours: what is allocated, what is still open, and how a project's forecast sits against its
// budget hours. Reuses utilizationLogic's grid so scenarios and pipeline behave identically.
import { scenarioDemand } from './utilizationLogic';

/** Open hours below this per month count as "thin" (two working days of slack). */
export const THIN_HOURS_PER_MONTH = 16;

export function remainingBand(remaining, months = 1, capacity = 0) {
  if (!(capacity > 0) && remaining >= 0) return 'none';
  if (Math.round(remaining) < 0) return 'over';
  return remaining < THIN_HOURS_PER_MONTH * months ? 'thin' : 'open';
}

/**
 * Budget vs. logged vs. forecast, per project and team, in hours.
 *   remaining = budget - hours logged to date - forecast from this month on.
 * Budgets are sparse today, so a row with no budget has `remaining: null` ("not set"), never zero --
 * a zero would read as "fully spent". Forecast only counts from `nowIdx` so months already logged
 * aren't counted twice.
 */
export function budgetCompare(feed, { teamIds = [], includePipeline = true, scenarios = {}, nowIdx = 0 }) {
  const teamName = new Map(feed.teams.map((t) => [t.id, t.name]));
  const teamOk = (id) => !teamIds.length || (id != null && teamIds.includes(id));
  const projects = new Map(feed.projects.filter((p) => includePipeline || !p.isPipeline).map((p) => [p.id, p]));
  const cells = new Map(); // `${projectId}|${teamId}` -> { projectId, teamId, budget, actual, forecast }
  const cell = (projectId, teamId) => {
    const key = `${projectId}|${teamId}`;
    if (!cells.has(key)) cells.set(key, { projectId, teamId, budget: 0, actual: 0, forecast: 0, hasBudget: false });
    return cells.get(key);
  };
  for (const b of feed.budgets || []) if (projects.has(b.projectId) && teamOk(b.teamId)) { const c = cell(b.projectId, b.teamId); c.budget += b.hours; c.hasBudget = true; }
  for (const a of feed.actuals || []) if (projects.has(a.projectId) && teamOk(a.teamId)) cell(a.projectId, a.teamId).actual += a.hours;
  for (const d of scenarioDemand(feed, scenarios, nowIdx)) {
    if (d.i >= nowIdx && projects.has(d.projectId) && teamOk(d.teamId)) cell(d.projectId, d.teamId).forecast += d.hours;
  }

  const finish = (c) => ({ ...c, remaining: c.hasBudget ? c.budget - c.actual - c.forecast : null });
  const byProject = new Map();
  for (const c of cells.values()) {
    if (!byProject.has(c.projectId)) byProject.set(c.projectId, []);
    byProject.get(c.projectId).push(c);
  }
  return [...byProject.entries()]
    .map(([projectId, list]) => {
      const total = finish(list.reduce((a, c) => ({ ...a, budget: a.budget + c.budget, actual: a.actual + c.actual, forecast: a.forecast + c.forecast, hasBudget: a.hasBudget || c.hasBudget }), { projectId, budget: 0, actual: 0, forecast: 0, hasBudget: false }));
      return {
        project: projects.get(projectId),
        ...total,
        teams: list.map(finish).map((c) => ({ ...c, name: c.teamId ? teamName.get(c.teamId) || 'Unknown team' : 'No team / project-level' }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      };
    })
    .sort((a, b) => a.project.name.localeCompare(b.project.name));
}

export const cellKey = (projectId, teamId, i) => `${projectId}|${teamId}|${i}`;

/**
 * The baseline forecast the editor works from: per project/team/month, hours named to people vs.
 * hours at team level. Only the team-level part is editable here -- once people are named for a cell
 * they ARE the forecast (TeamMonthlyAllocation's rule), so that cell stays read-only.
 */
export function forecastIndex(feed, { teamIds = [], includePipeline = true }) {
  const projects = new Map(feed.projects.filter((p) => includePipeline || !p.isPipeline).map((p) => [p.id, p]));
  const cells = new Map();
  const pairs = new Map();
  for (const d of feed.demand) {
    if (!projects.has(d.projectId) || (teamIds.length && !teamIds.includes(d.teamId))) continue;
    const i = d.year * 12 + d.month - 1;
    const key = cellKey(d.projectId, d.teamId, i);
    const c = cells.get(key) || { named: 0, team: 0 };
    if (d.userId) c.named += d.hours; else c.team += d.hours;
    cells.set(key, c);
    pairs.set(`${d.projectId}|${d.teamId}`, { projectId: d.projectId, teamId: d.teamId });
  }
  return { projects, cells, pairs };
}
